import { useToast } from '../../../../hooks/useToast';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Shield,
  Plus,
  Edit3,
  Check,
  Eye,
  EyeOff,
  Key,
  Lock,
  Sliders,
  AlertCircle
} from 'lucide-react';
import { RoleData } from '../../../../types/Role.types';
import './RolesView.css';
import { ModuleHeader } from '../../../../components/shared/ModuleHeader/ModuleHeader';
import { SidebarForm } from '../../../../components/shared/SidebarForm/SidebarForm';
import { DataTable, ColumnDef } from '../../../../components/shared/DataTable/DataTable';
import { TableSearch } from '../../../../components/shared/TableSearch/TableSearch';
import { MasterDetailLayout } from '../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, viewAction, editAction, deleteAction } from '../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { DetailPanel } from '../../../../components/shared/DetailPanel/DetailPanel';
import { DetailHeaderButton, DetailIdentity } from '../../../../components/shared/DetailPanel/DetailBlocks';
import { useClickOutside, isDetailKeepZone } from '../../../../hooks/useClickOutside';
import Modal from '../../../../components/shared/Modal/Modal';

import { UserData } from '../../../../types/User.types';
import { MOCK_ROLES } from '../../../../mocks/Roles.mocks';
import { MOCK_USERS } from '../../../../mocks/Users.mocks';
import { useApiPermissions } from '../../../../hooks/useApiPermissions';

interface RolesViewProps {
  onMenuOpen: () => void;
  menuCloseSignal: number;
}

type PrivilegeLevel = 'none' | 'view' | 'full';

// Privileges are keyed by the real `entity` of the permissions table, and the
// modules shown in the matrix come from GET /permissions-list — a new entity
// in the backend shows up here without touching the frontend. A missing key
// means 'none'.
const EMPTY_PRIVILEGES: RoleData['privileges'] = {};

// Entities a freshly created role can already see.
const DEFAULT_VIEW_ENTITIES = ['cuadro_de_mando', 'editorial', 'inventario', 'distribucion', 'almacenes', 'analitica'];

const entitiesOf = (...maps: RoleData['privileges'][]): string[] =>
  Object.keys(maps.reduce((acc, m) => ({ ...acc, ...m }), {} as RoleData['privileges']));

const levelOf = (privileges: RoleData['privileges'], entity: string): PrivilegeLevel => privileges[entity] || 'none';

export const RolesView = ({
  onMenuOpen,
  menuCloseSignal
}: RolesViewProps) => {
  const { notify } = useToast();
  const { permissions: apiPermissions } = useApiPermissions();
  const privilegeModules = useMemo(() => {
    const byEntity = new Map<string, { id: string; label: string; desc: string }>();
    apiPermissions.forEach(p => {
      if (!byEntity.has(p.entity)) byEntity.set(p.entity, { id: p.entity, label: p.entity.toUpperCase(), desc: p.description });
    });
    return Array.from(byEntity.values());
  }, [apiPermissions]);
  const [roles, setRoles] = useState<RoleData[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [users] = useState(MOCK_USERS); // We might need to fetch users if we want accurate user counts per role, but we'll leave it as is for now or fetch later.
  const [selectedRole, setSelectedRole] = useState<RoleData | null>(null);
  const [deleteModalItem, setDeleteModalItem] = useState<RoleData | null>(null);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchRoles = async () => {
      setLoading(true);
      try {
        let url = `/roles-list?page=${currentPage}&page_size=50`;
        if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
        
        const Cookies = (await import('universal-cookie')).default;
        const cookies = new Cookies();
        const userCookie = cookies.get('101Obex');
        if (userCookie && userCookie.token) {
          url += `&access_token=${encodeURIComponent(userCookie.token)}`;
        }

        const ObexRequestsHandlerP80 = (await import('../../../../handlers/ObexRequestsHandlerP80')).default;
        let res = await ObexRequestsHandlerP80.get(url);

        if (res.data) res = res.data;
        if (res.result) res = res.result;

        if (res && res.response && Array.isArray(res.response.data)) {
          const mappedRoles: RoleData[] = res.response.data.map((r: any) => ({
            id: String(r.id),
            name: r.name,
            description: r.description || '',
            isSystem: r.is_system_role || false,
            privileges: r.privileges || {}
          }));
          setRoles(mappedRoles);
          setTotalRecords(res.response.total || mappedRoles.length);
        } else if (res && Array.isArray(res.response)) {
          const mappedRoles: RoleData[] = res.response.map((r: any) => ({
            id: String(r.id),
            name: r.name,
            description: r.description || '',
            isSystem: r.is_system_role || false,
            privileges: r.privileges || {}
          }));
          setRoles(mappedRoles);
          setTotalRecords(mappedRoles.length);
        }
      } catch (error) {
        console.error("Error fetching roles:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, [searchQuery, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const initialFormStateRef = useRef<Omit<RoleData, 'id'>>({
    name: '',
    description: '',
    privileges: { ...EMPTY_PRIVILEGES }
  });

  const [formData, setFormData] = useState<Omit<RoleData, 'id'>>({
    name: '',
    description: '',
    privileges: { ...EMPTY_PRIVILEGES }
  });

  const isFormDirty = () => {
    const init = initialFormStateRef.current;
    if (formData.name !== init.name) return true;
    if (formData.description !== init.description) return true;
    for (const key of entitiesOf(formData.privileges, init.privileges)) {
      if (levelOf(formData.privileges, key) !== levelOf(init.privileges, key)) return true;
    }
    return false;
  };

  useEffect(() => {
    if (selectedRole) {
      const current = roles.find(r => r.id === selectedRole.id);
      if (current) {
        setSelectedRole(current);
      } else {
        setSelectedRole(null);
      }
    }
  }, [roles, selectedRole?.id]);

  useEffect(() => {
    if (selectedRole && !isFormOpen) {
      const data = {
        name: selectedRole.name,
        description: selectedRole.description,
        privileges: { ...selectedRole.privileges }
      };
      setFormData(data);
    }
  }, [selectedRole, isFormOpen]);

  // Clic fuera: cierra el panel (con el aviso de cambios sin guardar de
  // siempre). Las zonas con data-keep-detail y los portales no cierran.
  useClickOutside(isDetailKeepZone, () => {
    if (isFormOpen) {
      if (isFormDirty()) {
        setShowDiscardModal(true);
      } else {
        setSelectedRole(null);
        setIsFormOpen(false);
      }
    } else if (selectedRole) {
      setSelectedRole(null);
    }
  }, true);

  const handleCreateNewClick = () => {
    setSelectedRole(null);
    const initialData = {
      name: '',
      description: '',
      privileges: DEFAULT_VIEW_ENTITIES.reduce<RoleData['privileges']>((acc, entity) => { acc[entity] = 'view'; return acc; }, {})
    };
    initialFormStateRef.current = initialData;
    setFormData(initialData);
    setIsFormOpen(true);
  };

  // Fetches a single role's fresh details from GET /roles/get. Used to keep the
  // editor / detail drawer in sync with the backend when opening a role already
  // held in the `roles` list.
  // Derives the none/view/full matrix from GET /roles-permissions-list: a
  // role's real permission rows, each an entity + "read" or "all" action.
  const fetchRolePrivileges = async (roleId: string): Promise<RoleData['privileges']> => {
    try {
      const Cookies = (await import('universal-cookie')).default;
      const cookies = new Cookies();
      const userCookie = cookies.get('101Obex');
      if (!userCookie || !userCookie.token) return { ...EMPTY_PRIVILEGES };

      const ObexRequestsHandlerP80 = (await import('../../../../handlers/ObexRequestsHandlerP80')).default;
      let res = await ObexRequestsHandlerP80.get('/roles-permissions-list', {
        access_token: userCookie.token,
        role_id: roleId,
      });
      if (res.data) res = res.data;

      const rows = res?.response ?? res?.result?.response;
      const privileges: RoleData['privileges'] = { ...EMPTY_PRIVILEGES };
      if (Array.isArray(rows)) {
        rows.forEach((p: any) => {
          if (!p.entity) return;
          if (p.action === 'all') privileges[p.entity] = 'full';
          else if (p.action === 'read' && privileges[p.entity] !== 'full') privileges[p.entity] = 'view';
        });
      }
      return privileges;
    } catch (error) {
      console.error('Error fetching role permissions:', error);
      return { ...EMPTY_PRIVILEGES };
    }
  };

  const fetchRoleById = async (roleId: string): Promise<RoleData | null> => {
    try {
      const Cookies = (await import('universal-cookie')).default;
      const cookies = new Cookies();
      const userCookie = cookies.get('101Obex');
      if (!userCookie || !userCookie.token) return null;

      const ObexRequestsHandlerP80 = (await import('../../../../handlers/ObexRequestsHandlerP80')).default;
      let res = await ObexRequestsHandlerP80.get('/roles-get', {
        access_token: userCookie.token,
        role_id: roleId,
      });
      if (res.data) res = res.data;

      const roleRow = res?.response ?? res?.result?.response;
      if (roleRow && typeof roleRow === 'object' && roleRow.id) {
        return {
          id: String(roleRow.id),
          name: roleRow.name,
          description: roleRow.description || '',
          isSystem: roleRow.is_system_role || false,
          privileges: await fetchRolePrivileges(roleId),
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching role:', error);
      return null;
    }
  };

  // Selects a role (for the read-only detail panel, or to edit it), showing
  // the row already in hand immediately, then refreshing it from /roles/get.
  const selectRole = async (role: RoleData, openForm: boolean) => {
    setSelectedRole(role);
    setIsFormOpen(openForm);
    if (openForm) {
      const initialData = {
        name: role.name,
        description: role.description,
        privileges: { ...role.privileges }
      };
      initialFormStateRef.current = initialData;
      setFormData(initialData);
    }

    const freshRole = await fetchRoleById(role.id);
    if (freshRole) {
      setSelectedRole(freshRole);
      if (openForm) {
        const freshData = {
          name: freshRole.name,
          description: freshRole.description,
          privileges: { ...freshRole.privileges }
        };
        initialFormStateRef.current = freshData;
        setFormData(freshData);
      }
    }
  };

  const handleEditClick = (role: RoleData) => {
    selectRole(role, true);
  };

  // Reconciles each module from its current level to the desired one via
  // /roles-permissions-add and /roles-permissions-remove. 'view' is
  // represented by the "_read" row, 'full' by the "_all" row (see
  // Administrador — only "_all" rows, no "_read" alongside them), so a
  // level change is just adding/removing those two rows as needed.
  const syncRolePrivileges = async (
    roleId: string,
    token: string,
    original: RoleData['privileges'],
    desired: RoleData['privileges']
  ): Promise<void> => {
    const ObexRequestsHandlerP80 = (await import('../../../../handlers/ObexRequestsHandlerP80')).default;
    const changes: { entity: string; action: 'read' | 'all'; op: 'add' | 'remove' }[] = [];

    entitiesOf(original, desired).forEach((entity) => {
      const from = levelOf(original, entity);
      const to = levelOf(desired, entity);
      if (to === from) return;
      if (from === 'view') changes.push({ entity, action: 'read', op: 'remove' });
      if (from === 'full') changes.push({ entity, action: 'all', op: 'remove' });
      if (to === 'view') changes.push({ entity, action: 'read', op: 'add' });
      if (to === 'full') changes.push({ entity, action: 'all', op: 'add' });
    });

    await Promise.all(changes.map(async ({ entity, action, op }) => {
      const permission = apiPermissions.find(p => p.entity === entity && p.action === action);
      if (!permission) return;
      try {
        await ObexRequestsHandlerP80.get(op === 'add' ? '/roles-permissions-add' : '/roles-permissions-remove', {
          access_token: token,
          role_id: roleId,
          permission_id: permission.id,
        });
      } catch (error) {
        console.error(`Error ${op === 'add' ? 'granting' : 'revoking'} ${permission.code} for role ${roleId}:`, error);
      }
    }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      notify('Aviso', 'El nombre del rol es obligatorio.', 'warning');
      return;
    }

    try {
      const Cookies = (await import('universal-cookie')).default;
      const cookies = new Cookies();
      const userCookie = cookies.get('101Obex');
      const token = userCookie ? userCookie.token : '';

      const ObexRequestsHandlerP80 = (await import('../../../../handlers/ObexRequestsHandlerP80')).default;

      if (selectedRole) {
        // POST returns a 500 on this backend — GET is what actually works
        // (same gateway quirk seen on /create, /update, /roles-get, etc.).
        let res = await ObexRequestsHandlerP80.get('/roles-update', {
          access_token: token,
          role_id: selectedRole.id,
          name: formData.name,
          description: formData.description,
        });

        if (res.data) res = res.data;
        if (res.result) res = res.result;

        if (res && res.response && typeof res.response === 'object' && res.response.id) {
          const roleId = String(res.response.id);
          await syncRolePrivileges(roleId, token, selectedRole.privileges, formData.privileges);
          const finalPrivileges = await fetchRolePrivileges(roleId);

          const updatedRole: RoleData = {
            id: roleId,
            name: res.response.name,
            description: res.response.description || '',
            isSystem: res.response.is_system_role || false,
            privileges: finalPrivileges
          };
          setRoles(prev => prev.map(r => r.id === updatedRole.id ? updatedRole : r));
          setSelectedRole(updatedRole);
          notify('Éxito', 'Rol actualizado exitosamente', 'info');
        } else {
          notify('Error', 'No se pudo actualizar el rol', 'warning');
          return;
        }
      } else {
        // Same gateway quirk as above — GET is what actually works for /roles-create.
        let res = await ObexRequestsHandlerP80.get('/roles-create', {
          access_token: token,
          name: formData.name,
          description: formData.description,
        });

        if (res.data) res = res.data;
        if (res.result) res = res.result;

        if (res && res.response && typeof res.response === 'object' && res.response.id) {
          const roleId = String(res.response.id);
          // A brand-new role starts with no permission rows at all.
          await syncRolePrivileges(roleId, token, EMPTY_PRIVILEGES, formData.privileges);
          const finalPrivileges = await fetchRolePrivileges(roleId);

          const newRole: RoleData = {
            id: roleId,
            name: res.response.name,
            description: res.response.description || '',
            isSystem: res.response.is_system_role || false,
            privileges: finalPrivileges
          };
          setRoles(prev => [...prev, newRole]);
          setTotalRecords(prev => prev + 1);
          notify('Éxito', 'Rol creado exitosamente', 'info');
        } else {
          notify('Error', 'No se pudo crear el rol', 'warning');
          return;
        }
      }
      setIsFormOpen(false);
    } catch (error) {
      console.error("Error saving role:", error);
      notify('Error', 'Error de red en la operación', 'urgent');
    }
  };

  const handleDelete = async (roleId: string) => {
    const role = roles.find(r => r.id === roleId);
    if (role?.isSystem) return;

    const usersWithRole = users.filter(u => u.role === roleId);
    if (usersWithRole.length > 0) {
      notify('Error', `No se puede eliminar: el rol está asignado a ${usersWithRole.length} usuario(s)`, 'warning');
      return;
    }

    setDeleteModalItem(role!);
  };

  const confirmDelete = async () => {
    if (!deleteModalItem) return;
    const roleId = deleteModalItem.id;

    try {
      const Cookies = (await import('universal-cookie')).default;
      const cookies = new Cookies();
      const userCookie = cookies.get('101Obex');
      const token = userCookie ? userCookie.token : '';

      const ObexRequestsHandlerP80 = (await import('../../../../handlers/ObexRequestsHandlerP80')).default;

      // POST /roles-delete returns a 500 on this backend — GET is what actually works.
      let res = await ObexRequestsHandlerP80.get('/roles-delete', {
        access_token: token,
        role_id: roleId,
      });

      if (res.data) res = res.data;
      if (res.result) res = res.result;

      if (typeof res?.response === 'string' && res.response.toLowerCase().includes('eliminado')) {
        setRoles(prev => prev.filter(r => r.id !== roleId));
        setTotalRecords(prev => Math.max(0, prev - 1));
        if (selectedRole?.id === roleId) {
          setSelectedRole(null);
          setIsFormOpen(false);
        }
        notify('Éxito', 'Rol eliminado exitosamente', 'info');
      } else {
        notify('Error', 'No se pudo eliminar el rol', 'warning');
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      notify('Error', 'Error de red al eliminar el rol', 'urgent');
    } finally {
      setDeleteModalItem(null);
    }
  };

  const updatePrivilege = (moduleId: string, value: PrivilegeLevel) => {
    setFormData(prev => ({
      ...prev,
      privileges: {
        ...prev.privileges,
        [moduleId]: value
      }
    }));
  };

  const filteredRoles = roles;

  const columns: ColumnDef<RoleData>[] = [
    {
      id: 'id',
      header: 'ID_REF',
      render: (role) => <span className="roles-table-id">{role.id.toUpperCase()}</span>
    },
    {
      id: 'name',
      header: 'Nombre',
      render: (role) => (
        <span className="roles-table-name">{role.name}</span>
      )
    },
    {
      id: 'description',
      header: 'Descripción',
      render: (role) => (
        <div className="roles-table-desc" title={role.description}>
          {role.description}
        </div>
      )
    },
    {
      id: 'cuentas',
      header: 'Cuentas',
      render: (role) => {
        const userCount = users.filter(u => u.role === role.id).length;
        return <span className="roles-table-count">{userCount}</span>;
      },
      align: 'center'
    },
    {
      id: 'tipo',
      header: 'Tipo',
      render: (role) => (
        <span className={`roles-table-type ${role.isSystem ? 'system' : 'personal'}`}>
          {role.isSystem ? 'SISTEMA' : 'PERSONAL'}
        </span>
      )
    }
  ];

  const renderActions = (role: RoleData) => (
    <RowActionsMenu
      actions={[
        viewAction(() => selectRole(role, false)),
        editAction(() => handleEditClick(role)),
        // Los roles del sistema no se pueden borrar: la opción no aparece.
        ...(role.isSystem ? [] : [deleteAction(() => handleDelete(role.id))])
      ]}
    />
  );

  // X del formulario: cierra el panel, avisando si hay cambios.
  const closeForm = () => {
    if (isFormDirty()) {
      setShowDiscardModal(true);
    } else {
      setSelectedRole(null);
      setIsFormOpen(false);
    }
  };

  const renderPrivilegeIcon = (value: 'none' | 'view' | 'full') => {
    switch (value) {
      case 'full': return <Check size={14} className="roles-privilege-icon full" />;
      case 'view': return <Eye size={14} className="roles-privilege-icon view" />;
      case 'none': return <EyeOff size={14} className="roles-privilege-icon none" />;
    }
  };

  const renderPrivilegeLabel = (value: 'none' | 'view' | 'full') => {
    switch (value) {
      case 'full': return 'COMPLETO';
      case 'view': return 'SOLO LECTURA';
      case 'none': return 'SIN ACCESO';
    }
  };

  return (
    <>
      <MasterDetailLayout isOpen={Boolean(selectedRole || isFormOpen)} drawer>
        <div className="roles-view-container">
          <div className="roles-view-header-container">
            <ModuleHeader
              title="Gestión_de_Roles_y_Seguridad"
              subtitle="Defina privilegios, accesos por módulo y perfiles de seguridad para el personal."
            />
            <div className="roles-view-header-actions">
              <button onClick={handleCreateNewClick} className="roles-view-create-btn" data-keep-detail>
                <Plus size={14} />
                Nuevo_Rol
              </button>
            </div>
          </div>

          <div className="roles-view-filters-container">
            <TableSearch
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="BUSCAR ROLES POR NOMBRE O DESCRIPCIÓN..."
            />
          </div>

          <DataTable
            data={filteredRoles}
            columns={columns}
            title="Perfiles_y_Roles_de_Seguridad"
            subtitle={`TOTAL: ${totalRecords}`}
            showPagination={true}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            isLoading={loading}
            totalRecords={totalRecords}
            getRowId={(role) => role.id}
            onRowClick={(role) => selectRole(role, false)}
            selectedRowId={selectedRole?.id}
            actionsColumn={renderActions}
            emptyText="No hay roles registrados en el sistema."
          />
        </div>

        {isFormOpen ? (
          <SidebarForm
            drawer
            title={selectedRole ? 'EDITAR_ROL_ACCESO' : 'CREAR_NUEVO_ROL_ACCESO'}
            onClose={closeForm}
            submitLabel={selectedRole ? 'Actualizar Rol' : 'Registrar Rol'}
            onSubmit={handleSave}
            showDelete={Boolean(selectedRole && !selectedRole.isSystem)}
            onDelete={() => selectedRole && handleDelete(selectedRole.id)}
          >
            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Nombre del Rol</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData(prev => ({ ...prev, name: val }));
                }}
                className="sidebar-form-input"
                placeholder="EJ. RESPONSABLE DE TIENDAS"
                disabled={selectedRole?.isSystem}
              />
            </div>
            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Descripción / Nota</label>
              <input
                type="text"
                required
                value={formData.description}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData(prev => ({ ...prev, description: val }));
                }}
                className="sidebar-form-input"
                placeholder="Ej. Acceso completo a inventario y ventas minoristas."
              />
            </div>

            <div className="roles-form-matrix-section">
              <div className="roles-form-matrix-title">Matriz_de_Privilegios_y_Acceso</div>
              <div className="roles-form-matrix-grid">
                {privilegeModules.map(module => (
                  <div key={module.id} className="roles-form-matrix-item">
                    <div className="roles-form-matrix-info">
                      <span className="roles-form-matrix-name">{module.label}</span>
                      <p className="roles-form-matrix-desc">{module.desc}</p>
                    </div>
                    <div className="roles-form-matrix-controls">
                      <button
                        type="button"
                        onClick={() => !selectedRole?.isSystem && updatePrivilege(module.id, 'none')}
                        className={`roles-form-matrix-btn ${levelOf(formData.privileges, module.id) === 'none' ? 'active' : ''}`}
                        disabled={selectedRole?.isSystem}
                      >
                        NINGUNO
                      </button>
                      <button
                        type="button"
                        onClick={() => !selectedRole?.isSystem && updatePrivilege(module.id, 'view')}
                        className={`roles-form-matrix-btn ${levelOf(formData.privileges, module.id) === 'view' ? 'active' : ''}`}
                        disabled={selectedRole?.isSystem}
                      >
                        LECTURA
                      </button>
                      <button
                        type="button"
                        onClick={() => !selectedRole?.isSystem && updatePrivilege(module.id, 'full')}
                        className={`roles-form-matrix-btn ${levelOf(formData.privileges, module.id) === 'full' ? 'active' : ''}`}
                        disabled={selectedRole?.isSystem}
                      >
                        TOTAL
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </SidebarForm>
        ) : selectedRole ? (
          <DetailPanel
            drawer
            title="Ficha_De_Rol"
            onClose={() => {
              setSelectedRole(null);
              setIsFormOpen(false);
            }}
            headerActions={
              <>
                {selectedRole.isSystem && (
                  <div className="roles-details-badge">
                    <Lock size={12} />
                    Rol Protegido
                  </div>
                )}
                <DetailHeaderButton icon={Edit3} label="Editar" onClick={() => handleEditClick(selectedRole)} />
              </>
            }
          >
            <DetailIdentity
              icon={Shield}
              reference="// PERFIL_DE_SEGURIDAD"
              name={selectedRole.name}
              subtitle={selectedRole.description}
            />

            {selectedRole.isSystem && (
              <div className="roles-system-info-alert">
                <AlertCircle size={14} className="roles-system-info-icon" />
                <span className="roles-system-info-text">
                  Este es un rol del sistema preconfigurado con privilegios protegidos que no pueden ser modificados.
                </span>
              </div>
            )}

            <div className="roles-matrix-section">
              <div className="roles-matrix-title">Matriz_de_Privilegios_y_Acceso</div>
              <div className="roles-matrix-grid">
                {privilegeModules.map(module => {
                  const val = levelOf(selectedRole.privileges, module.id);
                  return (
                    <div key={module.id} className="roles-matrix-item">
                      <div className="roles-matrix-item-info">
                        <span className="roles-matrix-item-name">{module.label}</span>
                        <p className="roles-matrix-item-desc">{module.desc}</p>
                      </div>
                      <div className={`roles-matrix-status ${val}`}>
                        {val === 'full' && <Key size={10} />}
                        {val === 'view' && <Eye size={10} />}
                        {val === 'none' && <EyeOff size={10} />}
                        <span>{renderPrivilegeLabel(val)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </DetailPanel>
        ) : null}
      </MasterDetailLayout>

      <Modal
        isOpen={!!deleteModalItem}
        type="urgent"
        title="Eliminar rol"
        message={`¿Está seguro de que desea eliminar el rol "${deleteModalItem?.name}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteModalItem(null)}
      />

      <Modal
        isOpen={showDiscardModal}
        type="warning"
        title="Descartar cambios"
        message="¿Desea descartar los cambios no guardados y salir del formulario, o prefiere continuar editando?"
        confirmText="Descartar y salir"
        cancelText="Continuar editando"
        onConfirm={() => {
          setShowDiscardModal(false);
          setSelectedRole(null);
          setIsFormOpen(false);
        }}
        onCancel={() => setShowDiscardModal(false)}
      />
    </>
  );
};
